import { Recurrence } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import * as _ from 'underscore';
import { FeatureFlags } from '../FeatureFlags';
import { IRemoteRefreshScheduleTypeNameFilter } from '../filters/RemoteRefreshScheduleTypeNameFilter';
import { IActionContext } from '../helpers/ActionContext';
import { RemoteRefreshScheduleEditorViewModel } from '../helpers/CronExpressionHelper';
import { L10n } from '../L10n/L10n';
import { IRemoteRefreshScheduleInfo } from '../models/IRemoteRefreshScheduleInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { RemoteRefreshSchedules } from '../resources/RemoteRefreshSchedules';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ChangeScheduleAction } from './ChangeScheduleAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ContentActionNotification } from './ContentActionNotification';
import { ICreateRemoteRefreshScheduleScope, ICreateRemoteRefreshSelfServiceScheduleScope } from './CreateRemoteRefreshScheduleAction';
import { ModalService } from './ModalService';

export class EditRemoteRefreshScheduleAction {

  public static $inject = [
    '$translate',
    '$filter',
    'ConfirmActionDialog',
    'ContentActionNotification',
    'RemoteRefreshSchedules'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $filter: ng.IFilterService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private ContentActionNotification: ContentActionNotification,
    private RemoteRefreshSchedules: RemoteRefreshSchedules) { }

  public execute(context: IActionContext,
      item: INamedItemInfo,
      schedule: IRemoteRefreshScheduleInfo,
      availableTypes: string[],
      changeScheduleAction: ChangeScheduleAction): ng.IPromise<IResult> {

    if (FeatureFlags.remoteRefreshSelfServiceScheduleWidgetEnabled) {
      return this.openDialogWithSSSWidget(context, item, schedule, availableTypes, changeScheduleAction);
    }

    return this.openDialogWithOldWidget(context, item, schedule, availableTypes);
  }

  private openDialogWithOldWidget(context: IActionContext,
    item: INamedItemInfo,
    schedule: IRemoteRefreshScheduleInfo,
    availableTypes: string[]) : ng.IPromise<IResult> {
      const scope = <ICreateRemoteRefreshScheduleScope> context.scope.$new();
      scope.availableTypeOptions = availableTypes;
      scope.minuteIncrementOptions = RemoteRefreshScheduleEditorViewModel.minuteIncrementOptions;
      scope.prompt = this.$translate.instant('editRemoteRefreshScheduleCustomDialogPrompt', {DATASOURCE_NAME: L10n.elideIfLong(item.name)});
      scope.startOnMonday = L10n.firstDayOfWeekIsMonday();
      scope.editorViewModel = new RemoteRefreshScheduleEditorViewModel();
      scope.editorViewModel.setFromCronExpression(schedule.definition);
      scope.scheduleInfo = _.clone(schedule);

      const initialViewModel = new RemoteRefreshScheduleEditorViewModel().clone(scope.editorViewModel);

      const action = () => {
        scope.scheduleInfo.definition = scope.editorViewModel.getCronExpression();
        return this.RemoteRefreshSchedules.update(item.id, schedule, scope.scheduleInfo);
      };
      const confirmDisabled = () => {
        return !scope.editorViewModel.isValid() ||
          (scope.editorViewModel.isEqual(initialViewModel) && scope.scheduleInfo.refreshType === schedule.refreshType);
      };
      return this.openDialog(context, 'confirmActionDialogRemoteRefreshSchedule.html',
        action, confirmDisabled, scope, () => scope.scheduleInfo.refreshType);
  }

  private openDialogWithSSSWidget(context: IActionContext,
    item: INamedItemInfo,
    schedule: IRemoteRefreshScheduleInfo,
    availableTypes: string[],
    changeScheduleAction: ChangeScheduleAction) : ng.IPromise<IResult> {
      const scope = <ICreateRemoteRefreshSelfServiceScheduleScope>context.scope.$new();
      changeScheduleAction.setupSelfServiceScheduleDialogScope(scope,
        schedule.scheduleDetails, true, schedule.timeZone, schedule.scheduleType,
        SelfServiceSchedulePaneType.BridgeExtractRefresh);
      scope.availableTypeOptions = availableTypes;
      scope.scheduleInfo = {
        refreshType: schedule.refreshType,
      };

      const action = () => {
        const recurrenceModel: Recurrence = scope.selfServiceSchedulePaneProps.recurrenceModel;
        const finalSchedule: IRemoteRefreshScheduleInfo = {
          id: schedule.id,
          contentType: schedule.contentType,
          definition: null,
          scheduleDetails: recurrenceModel.recurrenceDetails,
          scheduleType: recurrenceModel.recurrenceType,
          refreshType: scope.scheduleInfo.refreshType
        };
        return this.RemoteRefreshSchedules.update(item.id, schedule, finalSchedule);
      };
      const confirmDisabled = () => false;

      return this.openDialog(context, 'confirmActionDialogRemoteRefreshSelfServiceSchedule.html',
        action, confirmDisabled, scope, () => scope.scheduleInfo.refreshType);
  }

  private openDialog(context: IActionContext,
    bodyTemplateUrl: string,
    action: (confirmActionDialogInstance: IConfirmActionDialogInstance) => any,
    confirmDisabled: () => boolean,
    scope: ng.IScope,
    getRefreshType: () => string): ng.IPromise<IResult> {
    const dialogInstance = this.ConfirmActionDialog.open({
      title: this.$translate.instant('editRemoteRefreshScheduleDialogTitle'),
      bodyTemplateUrl: bodyTemplateUrl,
      size: '500px',
      scope: scope,
      position: _.extend(ModalService.anchoredTo(context.targetElement), {reserveHeight: 600}),
      actionName: this.$translate.instant('editRemoteRefreshSchedule'),
      action: action,
      confirmDisabled: confirmDisabled,
    });
    return dialogInstance.result
    .then((result: IResult) => {
      if (result.errors && result.errors.length) {
        const errorMessage = this.errorMessageFor(result.errors[0]);
        dialogInstance.showErrorMessage(errorMessage);
      } else {
        this.ContentActionNotification.notifySuccess(
          this.$translate.instant('editRemoteRefreshScheduleSuccess',
            { REFRESH_TYPE_NAME: this.$filter<IRemoteRefreshScheduleTypeNameFilter>('remoteRefreshScheduleTypeName')(getRefreshType()) })
        );
      }
      return result;
    })
    .finally(() => scope.$destroy());
  }

  private errorMessageFor(error: IError) {
    return this.$translate.instant('errorUnknown');
  }

}

Module.instance().service('EditRemoteRefreshScheduleAction', EditRemoteRefreshScheduleAction);
